// Контракт API пульта (задача D1): «Готовность» — строки проверок, пульс, снимок.
//
// `GET /api/v1/readiness` и `POST /api/v1/readiness/run` → `ReadinessSnapshot`. Строка (`CheckView`) — поля строки
// A14 (`CheckResult`, `src/console/readiness-model.ts`) как есть плюс `waitingFor` и `timedOut`. Постоянные списки
// (`CHECK_ORDER`, названия, `CHECK_NEEDED_FROM`) — копия реестра A14: экран рисует их до ответа API; совпадение держит
// `tests/console/contract.test.ts`.
import { z } from 'zod';
import { MomentSchema, StageSchema, type Stage } from './common.js';
import { VerdictSchema } from './status.js';

/** Статус строки: в порядке, внимание, сбой, ждёт (другую проверку или ступень, на которой станет нужна). */
export const CHECK_STATUSES = ['ok', 'warn', 'fail', 'waiting'] as const;
export const CheckStatusSchema = z.enum(CHECK_STATUSES);
export type CheckStatus = z.infer<typeof CheckStatusSchema>;

/** Группа строки: «Мешает сейчас», «К следующей ступени», «В порядке», «Понадобится». Решает её сервер (`groupOf` A14). */
export const CHECK_GROUPS = ['blocking_now', 'next_stage', 'ok', 'later'] as const;
export const CheckGroupSchema = z.enum(CHECK_GROUPS);
export type CheckGroup = z.infer<typeof CheckGroupSchema>;

/** Разделы справки: «Настройка и запуск», «Что делать, если что-то не так»; раздел внутри — по id проверки. */
export const HELP_DOCS = ['runbook-setup', 'runbook-troubleshooting'] as const;
export const HelpDocSchema = z.enum(HELP_DOCS);
export type HelpDoc = z.infer<typeof HelpDocSchema>;

/** Порядок проверок — порядок зависимостей (UX §1, таблица ORDER.md): первыми настройки, без них не работает ничего. */
export const CHECK_ORDER = [
  'settings',
  'worker',
  'openai_key',
  'openai_limit',
  'langfuse',
  'omni_read',
  'omni_files',
  'omni_api_login',
  'omni_mail_columns',
  'bitrix_webhook',
  'bitrix_targets',
  'dadata',
  'kb_published',
  'stoplist_active',
  'examples_ready',
  'last_eval',
  'channels',
  'duty_assigned',
  'sales_handover',
  'budget',
] as const;
export type CheckId = (typeof CHECK_ORDER)[number];

/** Названия проверок — постоянный список: экран рисует их до ответа API. */
export const CHECK_TITLES_RU: Readonly<Record<CheckId, string>> = {
  settings: 'Настройки читаются',
  worker: 'Обработчик сообщений',
  openai_key: 'Ключ OpenAI',
  openai_limit: 'Лимит проекта OpenAI',
  langfuse: 'Langfuse',
  omni_read: 'Чтение Омнидеска',
  omni_files: 'Учётка «Анна (ИИ)»',
  omni_api_login: 'Вход учётки в API Омнидеска',
  omni_mail_columns: 'Колонки почты Омнидеска',
  bitrix_webhook: 'Вебхук Битрикса',
  bitrix_targets: 'Кнопки и стадии сделок',
  dadata: 'DaData',
  kb_published: 'База знаний',
  stoplist_active: 'Стоп-лист',
  examples_ready: 'Примеры ответов',
  last_eval: 'Последний прогон',
  channels: 'Каналы',
  duty_assigned: 'Дежурные',
  sales_handover: 'Передача в продажи',
  budget: 'Бюджет',
};

/** Статус словами. */
export const CHECK_STATUS_RU: Readonly<Record<CheckStatus, string>> = {
  ok: 'в порядке',
  warn: 'внимание',
  fail: 'сбой',
  waiting: 'ждёт',
};

/**
 * С какой ступени проверка нужна; `null` — только после запуска. Путь запуска по решению Влада 05.10 — тень → сразу
 * 100 %: «Подсказки» и доли 10 и 50 % — вне обязательного пути.
 */
export const CHECK_NEEDED_FROM: Readonly<Record<CheckId, Stage | null>> = {
  settings: 'shadow',
  worker: 'shadow',
  openai_key: 'shadow',
  openai_limit: 'shadow',
  langfuse: 'shadow',
  omni_read: 'shadow',
  omni_files: 'shadow',
  omni_api_login: 'live100',
  omni_mail_columns: 'shadow',
  bitrix_webhook: 'live100',
  bitrix_targets: 'live100',
  dadata: null,
  kb_published: 'shadow',
  stoplist_active: 'shadow',
  examples_ready: null,
  last_eval: null,
  channels: 'shadow',
  duty_assigned: 'live100',
  sales_handover: 'live100',
  budget: 'shadow',
};

/** «Начните {from} — от {pronoun} зависят …» — для вердикта «Не настроена». */
export const CHECK_START_RU: Readonly<Record<CheckId, { readonly from: string; readonly pronoun: 'него' | 'неё' | 'них' }>> = {
  settings: { from: 'с настроек', pronoun: 'них' },
  worker: { from: 'с обработчика сообщений', pronoun: 'него' },
  openai_key: { from: 'с ключа OpenAI', pronoun: 'него' },
  openai_limit: { from: 'с лимита проекта OpenAI', pronoun: 'него' },
  langfuse: { from: 'с Langfuse', pronoun: 'него' },
  omni_read: { from: 'с чтения Омнидеска', pronoun: 'него' },
  omni_files: { from: 'с учётки «Анна (ИИ)»', pronoun: 'неё' },
  omni_api_login: { from: 'с учётки «Анна (ИИ)»', pronoun: 'неё' },
  omni_mail_columns: { from: 'с прав роли на колонки почты', pronoun: 'них' },
  bitrix_webhook: { from: 'с вебхука Битрикса', pronoun: 'него' },
  bitrix_targets: { from: 'с кнопок и стадий сделок', pronoun: 'них' },
  dadata: { from: 'с ключа DaData', pronoun: 'него' },
  kb_published: { from: 'с базы знаний', pronoun: 'неё' },
  stoplist_active: { from: 'со стоп-листа', pronoun: 'него' },
  examples_ready: { from: 'с примеров', pronoun: 'них' },
  last_eval: { from: 'с прогона проверочного набора', pronoun: 'него' },
  channels: { from: 'с каналов', pronoun: 'них' },
  duty_assigned: { from: 'с дежурных', pronoun: 'них' },
  sales_handover: { from: 'с передачи в продажи', pronoun: 'неё' },
  budget: { from: 'с бюджета', pronoun: 'него' },
};

/**
 * Строка «Готовности». Поля, кроме `waitingFor` и `timedOut`, — строка A14 как есть: «что не так» (`whatRu`),
 * подробности, «чем грозит» (`threatRu`, у строки в порядке — пустая строка), шаги «Как исправить», раздел справки,
 * момент проверки. `waitingFor` — проверка, которую ждёт строка в статусе «ждёт» (`null` — ждёт ступень или не ждёт);
 * `timedOut` — внешний сервис не ответил за свою границу (тогда «внимание» и «{сервис} не ответил за N с»).
 */
export const CheckViewSchema = z.object({
  id: z.string().min(1),
  titleRu: z.string(),
  status: CheckStatusSchema,
  group: CheckGroupSchema,
  neededFrom: StageSchema.nullable(),
  dependsOn: z.array(z.string()),
  waitingFor: z.string().nullable(),
  whatRu: z.string(),
  detailRu: z.string().nullable(),
  threatRu: z.string(),
  steps: z.array(z.string()),
  helpDoc: HelpDocSchema.nullable(),
  checkedAt: MomentSchema,
  timedOut: z.boolean(),
});
export type CheckView = z.infer<typeof CheckViewSchema>;

/**
 * Пульс за окно `from`–`to` (последний час): ходы Анны (без заменённых), из них ошибок, входящие клиентов (кроме
 * последних минут, пока серия ещё склеивается), из них без хода в своём диалоге (`unanswered`, кроме диалогов, где ход
 * идёт сейчас; самое раннее — `unansweredSince`), последний ход (за всё время), были ли ходы вообще и тревога
 * «сообщения клиентов без хода».
 */
export const PulseSchema = z.object({
  from: MomentSchema,
  to: MomentSchema,
  turns: z.number().int().nonnegative(),
  errors: z.number().int().nonnegative(),
  inbound: z.number().int().nonnegative(),
  unanswered: z.number().int().nonnegative(),
  unansweredSince: MomentSchema.nullable(),
  lastTurnAt: MomentSchema.nullable(),
  everTurned: z.boolean(),
  alarm: z.boolean(),
});
export type Pulse = z.infer<typeof PulseSchema>;

/** Как часто «Готовность» проверяется сама, секунд. */
export const READINESS_EVERY_SEC = 60;

/**
 * Снимок «Готовности»: строки в порядке `CHECK_ORDER`, вердикт, пульс (`null` — базу ходов не прочитать), ступень
 * запуска (`null` — настройки не читаются), ступень, по которой разложены группы (`layoutStage`: та же, а без настроек —
 * самая строгая, 100 %), следующая ступень и момент прогона.
 */
export const ReadinessSnapshotSchema = z.object({
  checks: z.array(CheckViewSchema),
  verdict: VerdictSchema,
  pulse: PulseSchema.nullable(),
  stage: StageSchema.nullable(),
  layoutStage: StageSchema,
  nextStage: StageSchema.nullable(),
  checkedAt: MomentSchema,
  everySec: z.literal(READINESS_EVERY_SEC),
});
export type ReadinessSnapshot = z.infer<typeof ReadinessSnapshotSchema>;

/** Тело `POST /api/v1/readiness/run`: без `ids` — проверить всё заново; с `ids` — заново спросить внешние сервисы только у них. */
export const RunChecksBodySchema = z.object({ ids: z.array(z.string().min(1)).min(1).optional() });
export type RunChecksBody = z.infer<typeof RunChecksBodySchema>;
